export type SubtitleFormat =
  | "application/x-subrip"
  | "text/x-ssa"
  | "text/vtt";

/** Converts *plain text* subtitle file contents from/to supported formats */
export function convertSubtitle(
  input: string,
  { from, to }: { from: SubtitleFormat; to: SubtitleFormat },
): { text: string; error?: string } {
  if (from in DirectConverters && to in DirectConverters[from])
    return { text: DirectConverters[from][to](input) };
  if (from in SrcConverters && to in DstConverters)
    return { text: DstConverters[to](SrcConverters[from](input)) };

  return {
    text: input,
    error: `Subtitle conversion from ${from} to ${to} not possible.`,
  };
}

export function validateSubtitleFormat(
  format: string,
): format is SubtitleFormat {
  return (
    format in
    { "application/x-subrip": 1, "text/x-ssa": 1, "text/vtt": 1 }
  );
}

/**
 * Detects subtitle format by inspecting file contents, since the extension and
 * mime type reported by the browser on upload cannot be trusted.
 *
 * Timing lines look nearly identical between .srt and .vtt, differing only in
 * the decimal separator:
 * .srt: `00:00:01,000 --> 00:00:04,000`
 * .vtt: `00:00:01.000 --> 00:00:04.000`
 */
export function detectSubtitleFormat(text: string): SubtitleFormat | undefined {
  const trimmed = text.trimStart();

  if (/^WEBVTT/.test(trimmed)) return "text/vtt";
  if (/^\[Script Info\]/m.test(trimmed) || /^\[Events\]/m.test(trimmed))
    return "text/x-ssa";
  if (/^Dialogue:\s*\d/m.test(trimmed)) return "text/x-ssa";

  const timing = trimmed.match(
    /\d{2}:\d{2}:\d{2}[.,]\d{2,3}\s*-->\s*\d{2}:\d{2}:\d{2}[.,]\d{2,3}/,
  );
  if (timing) return timing[0].includes(",") ? "application/x-subrip" : "text/vtt";

  return undefined;
}

/** Internal intermediate structure when converting subtitles between formats */
type Line = { start: Timing; end: Timing; data: string[]; number?: number };
type Timing = { h: number; m: number; s: number; mili: number };

/** Functions parsing subtitles of given format into internal structure */
const SrcConverters = {
  /**
   * @name SRT-Source-Converter
   *
   * @description
   * This converter accepts SubRip (.srt) format and prepares the data for destination converters
   * by transforming it into a standardized list of objects with timing and text properties.
   *
   * @param {string} data - The SRT subtitle content as a string
   * @returns {Array} Array of subtitle objects with timing and text properties
   */
  "application/x-subrip": function (data: string) {
    var lines = [] as Line[];
    var index = 0;
    var previousLine: string;
    var currentLine = {} as Line;

    var addToStack = function () {
      lines[currentLine.number] = {
        start: currentLine.start,
        end: currentLine.end,
        data: currentLine.data,
      };
      currentLine = {} as Line;
    };

    data.split("\n").forEach(function (dataLine, dataIndex) {
      /* Empty space */
      if (!/\S/.test(dataLine)) {
        addToStack();

        previousLine = dataLine;
        return;
      }

      /* Index */
      if (
        (!/\S/.test(previousLine) || !previousLine) &&
        parseInt(dataLine, 10) !== null
      ) {
        currentLine.number = parseInt(dataLine, 10);

        previousLine = dataLine;
        return;
      }

      /* Timing */
      if (
        dataLine.indexOf("-->") !== -1 &&
        parseInt(previousLine, 10) !== null
      ) {
        var timings = dataLine.split(" --> ");

        currentLine.start = {
          h: parseInt(timings[0].split(":")[0], 10),
          m: parseInt(timings[0].split(":")[1], 10),
          s: parseInt(timings[0].split(":")[2], 10),
          mili: parseInt(timings[0].split(",")[1], 10),
        };
        currentLine.end = {
          h: parseInt(timings[1].split(":")[0], 10),
          m: parseInt(timings[1].split(":")[1], 10),
          s: parseInt(timings[1].split(":")[2], 10),
          mili: parseInt(timings[1].split(",")[1], 10),
        };

        previousLine = dataLine;
        return;
      }

      /* Subtitle itself */
      currentLine.data = Array.isArray(currentLine.data)
        ? currentLine.data
        : [];
      currentLine.data.push(dataLine);
      previousLine = dataLine;

      /* Last dialogue */
      if (dataIndex === data.split("\n").length - 1) {
        addToStack();
      }
    });

    lines = lines.filter(function (l) {
      return true;
    });

    return lines;
  },


  /**
   * @name SSA-Source-Converter
   *
   * @description
   * This converter is used to transform subtitles from the `text/x-ssa` format.
   * Although `text/x-ssa` is no longer a desired extension, we need to keep backwards compatibility.
   * Every time a user uploads new subtitles or descriptions, any existing ones must first be transformed
   * from `text/x-ssa` and then to `text/vtt`. This is necessary because Video.js does not support
   * various extensions for one player, causing errors if unsupported formats are present.
   *
   * @param {string} data - The SSA subtitle content as a string.
   * @returns {Array} Array of subtitle objects with timing and text.
   */
  "text/x-ssa": function (data: string) {
    const lines = [] as Line[];
    let inEvents = false;

    data.split("\n").forEach(function (line) {
      line = line.trim();

      // Check if we're in the Events section
      if (line === "[Events]") {
        inEvents = true;
        return;
      }

      // Skip non-dialogue lines
      if (!inEvents || !line.startsWith("Dialogue:")) {
        return;
      }

      // Parse dialogue line
      // Format: Dialogue: Layer,Start,End,Style,Name,MarginL,MarginR,MarginV,Effect,Text
      const parts = line.substring(9).split(","); // Remove 'Dialogue:' prefix
      if (parts.length < 10) return;

      // Extract timing information
      const startTime = parts[1];
      const endTime = parts[2];
      let text = parts.slice(9).join(","); // Rejoin text that might contain commas

      // Parse time format (H:MM:SS.CC)
      function parseTime(timeStr) {
        const match = timeStr.match(/(\d+):(\d{2}):(\d{2})\.(\d{2})/);
        if (!match) return { h: 0, m: 0, s: 0, mili: 0 };

        return {
          h: parseInt(match[1], 10),
          m: parseInt(match[2], 10),
          s: parseInt(match[3], 10),
          mili: parseInt(match[4], 10) * 10, // Convert centiseconds to milliseconds
        };
      }

      // Clean up text (remove SSA formatting)
      text = text.replace(/\\N/g, "\n"); // Replace \N with actual newlines
      text = text.replace(/\\n/g, "\n"); // Replace \n with actual newlines
      // replacement of the special tags
      text = text.replace(/\{\\\\b1}/g, "<b>").replace(/\{\\\\b0}/g, "</b>");
      text = text.replace(/\{\\\\i1}/g, "<i>").replace(/\{\\\\i0}/g, "</i>");
      text = text.replace(/\{\\\\u1}/g, "<u>").replace(/\{\\\\u0}/g, "</u>");
      text = text
        .replace(/\{\\\\1c&HCCFFFF&}/g, "<c.highlight>")
        .replace(/\{\\\\1c}/g, "</c>");

      lines.push({
        start: parseTime(startTime),
        end: parseTime(endTime),
        data: text.split("\n").filter(function (line) {
          return line.trim() !== "";
        }),
      });
    });

    return lines;
  },
} satisfies Record<string, (text: string) => Line[]>;

/** Functions converting internal structure to another format */
const DstConverters = {
  /**
   * @name SSA-Destination-Converter
   *
   * @description
   * @deprecated This converter is deprecated as we no longer use SSA format as a destination.
   * Our current destination format is .vtt, but we keep this converter for possible future use.
   *
   */
  "text/x-ssa": function (data: Line[]) {
    var result = "";
    result += "[Script Info]\n";
    result += "; Script generated by Junction\n";
    result += "; http://www.junctioneducation.com/\n";
    result += "ScriptType: v4.00+\n";
    result += "WrapStyle: 0\n";
    result += "PlayResX: 640\n";
    result += "PlayResY: 480\n";
    result += "ScaledBorderAndShadow: yes\n";
    result += "VideoAspectRatio: 0\n";
    result += "VideoZoom: 6\n";
    result += "VideoPosition: 0\n";
    result += "Last Style Storage: Default\n";
    result += "\n";
    result += "[V4+ Styles]\n";
    result +=
      "Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding\n";
    result +=
      "Style: Default,Arial,25,&H00FFFFFF,&H000000FF,&H00000000,&H00000000,0,0,0,0,100,100,0,0,3,1,1,2,10,10,30,1\n";
    result += "\n";
    result += "[Events]\n";
    result +=
      "Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text\n";

    function toFixed(number, len) {
      while ((number + "").length > len) {
        number = (number + "").substring(0, (number + "").length - 1);
      }

      while ((number + "").length < len) {
        number = "0" + number;
      }

      return number;
    }

    data.forEach(function (line) {
      result += "Dialogue: ";
      result += "0,";
      result +=
        toFixed(line.start.h, 1) +
        ":" +
        toFixed(line.start.m, 2) +
        ":" +
        toFixed(line.start.s, 2) +
        "." +
        toFixed(line.start.mili, 2) +
        ",";
      result +=
        toFixed(line.end.h, 1) +
        ":" +
        toFixed(line.end.m, 2) +
        ":" +
        toFixed(line.end.s, 2) +
        "." +
        toFixed(line.end.mili, 2) +
        ",";
      result += "Default,,0,0,0,,";
      result += line.data.join("\\N");
      result += "\n";
    });

    return result;
  },

  /**
   * @name VTT-Default-Destination-Converter
   *
   * @description
   * This is the default converter to VTT format expecting already transformed data in a list of objects.
   * The data should be pre-processed by a source converter first (e.g., SubConverterProvider.addSrcConverter('application/x-subrip'))
   * before using this destination converter to generate the final VTT output.
   *
   * @param {Array} data - Array of subtitle objects with timing and text properties
   * @returns {string} VTT formatted subtitle content
   */
  "text/vtt": function (data: Line[]) {
    var result = "WEBVTT\n\n";

    function toFixed(number, len) {
      var str = number.toString();
      while (str.length < len) {
        str = "0" + str;
      }
      return str;
    }

    function formatTime(time) {
      return (
        toFixed(time.h, 2) +
        ":" +
        toFixed(time.m, 2) +
        ":" +
        toFixed(time.s, 2) +
        "." +
        toFixed(time.mili, 3)
      );
    }

    data.forEach(function (line, index) {
      result += index + 1 + "\n";
      result += formatTime(line.start) + " --> " + formatTime(line.end) + "\n";
      result +=
        (Array.isArray(line.data) ? line.data.join("\n") : line.data) + "\n\n";
    });

    return result;
  },
} satisfies Record<string, (text: Line[]) => string>;

/** Converters that do not use the intermediate internal structure */
const DirectConverters = {
  /**
   * @name VTT-Fast-Destination-Converter
   *
   * @description
   *
   * This is the special way of transforming the subtitles available only for source .srt extension.
   * Since these two (.srt and .vtt) are very similar in the syntax it doesn't make much sense to
   * go through src converter.
   *
   */
  "application/x-subrip": {
    "text/vtt": function (data: string) {
      var lines = data.split(/\r?\n/);
      var result = "WEBVTT\n\n";
      var buffer = [];

      for (var i = 0; i < lines.length; i++) {
        var line = lines[i].trim();

        // Empty line closes the block
        if (line === "") {
          if (buffer.length > 0) {
            result += buffer.join("\n") + "\n\n";
            buffer = [];
          }
          continue;
        }

        // Timestamps: commans -> dots
        if (line.includes("-->")) {
          buffer.push(line.replace(/,/g, "."));
        } else {
          buffer.push(line);
        }
      }

      // Add last block if exists
      if (buffer.length > 0) {
        result += buffer.join("\n") + "\n\n";
      }
      return result;
    },
  },
} satisfies Record<string, Record<string, (text: string) => string>>;
